import React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchUsers, assignHtmlToUser, type UserItem } from "../api/endpoints/users";
import CustomDropdown from "./CustomDropdown";

interface AssignToManagerProps {
  className?: string;
  htmlId?: string; // template html/file id to assign
  onAssigned?: () => void;
}

export default function AssignToManager({ className, htmlId, onAssigned }: AssignToManagerProps) {
  const { data: users, isLoading: usersLoading, isError: usersError } = useQuery<UserItem[]>({
    queryKey: ["userlist"],
    queryFn: fetchUsers,
  });

  const [selectedUserId, setSelectedUserId] = React.useState<string>("");
  const [status, setStatus] = React.useState<null | { type: "success" | "error"; message: string }>(null);
  const statusTimerRef = React.useRef<number | null>(null);

  const clearStatusLater = React.useCallback(() => {
    if (statusTimerRef.current) window.clearTimeout(statusTimerRef.current);
    statusTimerRef.current = window.setTimeout(() => setStatus(null), 3000);
  }, []);

  React.useEffect(() => () => {
    if (statusTimerRef.current) window.clearTimeout(statusTimerRef.current);
  }, []);

  const { mutate: assignMutate, isPending: isAssigning } = useMutation({
    mutationFn: ({ userEmail, htmlId: id }: { userEmail: string; htmlId: string }) =>
      assignHtmlToUser({ userEmail, htmlId: id }),
    onSuccess: () => {
      setStatus({ type: "success", message: "Assigned successfully" });
      // Reset dropdown to default state after success
      setSelectedUserId("");
      clearStatusLater();
      setTimeout(() => {
        if (onAssigned) onAssigned();
      }, 2000);
    },
    onError: (err: unknown) => {
      const msg = typeof err === "object" && err && (err as { message?: string }).message ? (err as { message: string }).message : "Failed to assign";
      setStatus({ type: "error", message: msg });
      clearStatusLater();
    },
  });

  return (
    <div className={className}>
      {/* <label className="text-xs text-gray-600">Assign to Manager</label> */}
      {usersLoading ? (
        <div className="mt-2 flex items-center gap-2">
          <div className="h-8 w-72 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
        </div>
      ) : usersError ? (
        <div className="mt-2 text-xs text-red-500">Failed to load users</div>
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <CustomDropdown
            options={(users ?? []).map((u) => ({ value: u.id, label: u.name, hint: u.email }))}
            value={selectedUserId}
            onChange={setSelectedUserId}
            placeholder="Select manager"
            loading={usersLoading}
            searchMinChars={3}
            className="flex-auto"
            buttonClass="w-full"
          />
          <button
            onClick={() => {
              if (!selectedUserId || !htmlId) return;
              const selected = (users ?? []).find((u) => u.id === selectedUserId);
              const userEmail = selected?.email || "";
              if (!userEmail) {
                setStatus({ type: "error", message: "Selected user has no email" });
                clearStatusLater();
                return;
              }
              assignMutate({ userEmail, htmlId });
            }}
            disabled={!selectedUserId || !htmlId || isAssigning}
            className="h-8 min-w-32 text-xs px-3 py-1.5 btn-primary disabled:bg-gray-300 disabled:text-gray-500"
          >
            {isAssigning ? "Assigning..." : "Assign"}
          </button>
        </div>
      )}
      {status && (
        <div className="mt-2 text-xs">
          {status.type === "success" ? (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 text-green-700 border border-green-200">
              ✓ {status.message}
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-100 text-red-700 border border-red-200">
              ⚠ {status.message}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
